import { useContext, useEffect, useState } from "react";
import { JobsPanelState } from "../../../constants/Jobs";
import InputField from "../../genericComponents/InputField/InputField";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import { applyValidationResults, createJobAndSetAsPlan } from "../../../utils/jobsUtils";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import "./NewJob.css";
import "../EditedFeatures/EditedFeatures.css";

import CancelIcon from "../../../assets/cancel.svg?react";
import CloseIcon from "../../../assets/close.svg?react";
import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";
import SendIcon from "../../../assets/jobs/sendArrow.svg?react";

import Loader from "../../genericComponents/Loader/Loader";

import BackArrowIcon from "../../../assets/arrow-back.svg?react";
import { RightPanelTabs } from "../../../constants/Panels";
import { setRightPanelActiveTab } from "../../../store/appSlice";
import { StoreState } from "../../../store/rootReducer";
import { FeatureWithChanges, findDifferencesForObject } from "../../../utils/newVersioningUtils";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import { JobContext } from "../../../Context";
import EditedFeaturePanelForNewJob from "../EditedFeatures/EditedFeaturePanelForNewJob";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface NewJobForObjectProps {
    setJobPanelState: (state: JobsPanelState) => void;
    refreshJobs: () => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const NewJobForObject: React.FC<NewJobForObjectProps> = ({
    setJobPanelState,
    refreshJobs,
    storeHighlightHandle,
    clearHighlightedFeatures,
}) => {
    const dispatch = useDispatch();

    const jobsContext = useContext(JobContext);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const activeGuid = useSelector((state: StoreState) => state.version.activeGuid);

    const [loading, setLoading] = useState<boolean>(false);
    const [featureWithChanges, setFeatureWithChanges] = useState<FeatureWithChanges | null>(null);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);

    const [selectedFeaturesWithChanges, setSelectedFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);

    const [jobName, setJobName] = useState<string>("");
    const [jobDescription, setJobDescription] = useState<string>("");

    const [shouldCreateFDVObjects, setShouldCreateFDVObjects] = useState<boolean>(false);

    const addFeatureToJob = (featureToAdd: FeatureWithChanges) => {
        setSelectedFeaturesWithChanges([featureToAdd]);
    };

    // Single-object flow: removing simply deselects the one object (disables "Opprett plan").
    const removeFeatureFromJob = (_featureToRemove: FeatureWithChanges) => {
        setSelectedFeaturesWithChanges([]);
    };

    const getValidationResults = async (feature: FeatureWithChanges) => {
        const versionName = activeVersion?.versionIdentifier.name ?? null;
        await applyValidationResults(
            [feature],
            (updated) => {
                const validated = updated[0] ?? null;
                setFeatureWithChanges(validated);
                // Keep the selected feature in sync with the validated one (only if
                // still selected — don't re-select if the user deselected it).
                setSelectedFeaturesWithChanges((prev) => (validated && prev.length > 0 ? [validated] : prev));
            },
            versionName,
            "PostToDefault"
        );
    };

    const loadObject = async () => {
        const config = jobsContext?.preLoadJobConfig.value;
        // Consume the trigger config immediately: prevents the routing effect from
        // re-firing and avoids clearing shared context state after an unmount.
        jobsContext?.preLoadJobConfig.set(null);

        if (!config || !activeVersion) {
            setLoadFailed(true);
            return;
        }
        setLoading(true);
        try {
            const diffs = await findDifferencesForObject(
                activeGuid,
                activeVersion.versionIdentifier.name,
                config.layerID,
                config.objectId
            );
            const feature = diffs[0] ?? null;
            setFeatureWithChanges(feature);
            console.log("Objects with changes from job:", feature);
            if (feature) {
                addFeatureToJob(feature);
                getValidationResults(feature); // fire and forget
            } else {
                setLoadFailed(true);
            }
        } catch (error) {
            console.error("Error loading object for job:", error);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadObject();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createJobAndPlan = async () => {
        if (jobName === "") {
            toast.error("Jobbnavn må fylles ut");
            return;
        }
        if (jobName.length > 255) {
            toast.error("Jobbnavn kan ikke være lengre enn 255 tegn");
            return;
        }
        if (jobDescription === "") {
            toast.error("Beskrivelse må fylles ut");
            return;
        }
        if (jobDescription.length > 255) {
            toast.error("Beskrivelse kan ikke være lengre enn 255 tegn");
            return;
        }
        if (selectedFeaturesWithChanges.length === 0) {
            toast.error("Ingen objekter er tilknyttet jobben");
            return;
        }
        setLoading(true);

        let planVersion = "Version:" + activeVersion?.versionIdentifier.guid;
        const result = await createJobAndSetAsPlan(
            jobName,
            jobDescription,
            planVersion,
            selectedFeaturesWithChanges,
            activeVersion?.versionIdentifier.name ?? "",
            false,
            shouldCreateFDVObjects
        );

        if (result) {
            setJobName("");
            setJobDescription("");
            setSelectedFeaturesWithChanges([]);
            toast.success("Jobben er opprettet og satt som plan");
            refreshJobs();
            setJobPanelState(JobsPanelState.LIST);
            dispatch(triggerAlertsRefresh());
        }
        setLoading(false);
    };

    const canCreatePlan = () => {
        if (jobName === "") {
            return false;
        }
        if (jobDescription === "") {
            return false;
        }
        if (selectedFeaturesWithChanges.length === 0) {
            return false;
        }
        return true;
    };

    const closePanel = () => {
        setJobPanelState(JobsPanelState.LIST);
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
    };

    return (
        <div className="new-job-container">
            {loading && <Loader overlay="element" targetElement=".new-job-container" />}
            <div className="new-job-header w-100 flex-row space-between">
                <button className="empty-button center small-gap" onClick={() => setJobPanelState(JobsPanelState.LIST)}>
                    <BackArrowIcon />
                    <span>Tilbake</span>
                </button>
                <span>Opprett jobb på objekt</span>
                <button type="button" className="empty-button" onClick={closePanel}>
                    <CloseIcon />
                </button>
            </div>
            <div className="new-job-body">
                <div className="new-job-content">
                    <InputField
                        label="Jobbnavn"
                        w100
                        type="text"
                        onChange={setJobName}
                        value={jobName}
                        inputFieldId="new-job-name"
                        maxLength={255}
                    />
                    <InputField
                        label="Beskrivelse"
                        w100
                        type="text"
                        onChange={setJobDescription}
                        value={jobDescription}
                        inputFieldId="new-job-description"
                        isTextArea
                        textAreaRows={3}
                        maxLength={255}
                    />
                    <div className="flex-row small-gap center">
                        <CheckPinIcon />
                        {`Objekter tilknyttet jobben (${selectedFeaturesWithChanges.length})`}
                    </div>
                </div>

                <div className="edited-features w-100">
                    <div className="edited-features-body">
                        <div className="edited-features-content w-100 small-gap">
                            {!featureWithChanges && loadFailed && (
                                <div className="edited-features-no-changes">Fant ingen endringer for objektet</div>
                            )}
                            {featureWithChanges && (
                                <EditedFeaturePanelForNewJob
                                    key={`${featureWithChanges.mainFeature.layerId}-${featureWithChanges.mainFeature.objectId}`}
                                    editedFeature={featureWithChanges}
                                    addFeatureToJob={addFeatureToJob}
                                    removeFeatureFromJob={removeFeatureFromJob}
                                    featuresAddedToJob={selectedFeaturesWithChanges}
                                    cannotAddFeatureToJob={false}
                                    storeHighlightHandle={storeHighlightHandle}
                                    clearHighlightedFeatures={clearHighlightedFeatures}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="new-job-footer flex-col">
                <div className="flex-row small-gap">
                    <Checkbox
                        onChange={() => setShouldCreateFDVObjects(!shouldCreateFDVObjects)}
                        checked={shouldCreateFDVObjects}
                        id="create-fdv-objects"
                    />
                    <label htmlFor="create-fdv-objects" className={`checkbox-label`}>
                        Opprett FDV objekter for objekter som ikke allerede har det
                    </label>
                </div>

                <div className="flex-row small-gap">
                    <NfsButton
                        outlined
                        onClick={() => {
                            setJobPanelState(JobsPanelState.LIST);
                        }}
                    >
                        <span className="flex-row center small-gap">
                            <CancelIcon />
                            <span className="restore-text">Avbryt</span>
                        </span>
                    </NfsButton>
                    <NfsButton onClick={() => createJobAndPlan()} disabled={!canCreatePlan()}>
                        <span className="flex-row center small-gap">
                            <SendIcon className="no-flex" />
                            <span className="restore-text">Opprett plan</span>
                        </span>
                    </NfsButton>
                </div>
            </div>
        </div>
    );
};

export default NewJobForObject;
